import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { withRouter } from 'react-router';

import nds from '@nerdwallet/base-styles/classes';
import { Li, Link, List, Text } from '@nerdwallet/currency';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import {
  CONTENT_TABS,
  PREHEADER_TYPE,
  MarketplaceProductCardImpression,
  BaseMarketplaceProductCard,
} from '@nerdwallet/react-marketplace-product-card';
import { DISPLAY_PAGES } from '@nerdwallet/react-banking-product-cta';
import {
  PRODUCT_TYPES,
  BANKING_SUB_PRODUCT_TYPES,
  INVESTING_SUB_PRODUCT_TYPES,
} from '@nerdwallet/marketplace/constants';
import {
  getApplyLink,
  getLegacyId,
  getProductType,
  getSubProductType,
  getId,
} from '@nerdwallet/marketplace/selectors/common';
import { isNonMonetizing } from '@nerdwallet/marketplace/utils';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';
import { withMarketplaceContext } from '@nerdwallet/structured-content/contexts/MarketplaceContext';

import {
  boolCheckboxSpec,
  dataDependencyTypePicker,
  dataDependencyIdInput,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import { getSlug } from '../../../server/config-fetcher';
import { DATA_DEPENDENCY_TYPES } from '../../lib/constants';
import { getOfferById } from '../../lib/helpers';

import styles from './marketplace-product-card.module.less';

const PRODUCT_TYPE_NAME_MAPPING = {
  [PRODUCT_TYPES.CREDIT_CARDS]: 'Card',
  [BANKING_SUB_PRODUCT_TYPES.CHECKING]: 'Account',
  [PRODUCT_TYPES.BANKING]: 'Bank',
  [BANKING_SUB_PRODUCT_TYPES.CD]: 'CD',
  [BANKING_SUB_PRODUCT_TYPES.SAVINGS]: 'Account',
  [BANKING_SUB_PRODUCT_TYPES.MONEY_MARKET]: 'Account',
  [INVESTING_SUB_PRODUCT_TYPES.BROKER]: 'Broker',
  [INVESTING_SUB_PRODUCT_TYPES.INVESTMENT_ADVISOR]: 'Robo-advisor',
  [PRODUCT_TYPES.PERSONAL_LOANS]: 'Lender',
  [PRODUCT_TYPES.EDUCATION_LOANS]: 'Lender',
};

const PRODUCT_TYPE_UNSTRUCTURED_VERTICAL = {
  AUTOINSURANCE: 'auto_insurance',
  TAXES: 'taxes',
  TRAVEL: 'travel',
};

const getProductName = (entity) => {
  const productType = getProductType(entity);
  switch (productType) {
    case PRODUCT_TYPES.BANKING:
    case PRODUCT_TYPES.INVESTING:
      return (
        PRODUCT_TYPE_NAME_MAPPING[getSubProductType(entity)] ||
        PRODUCT_TYPE_NAME_MAPPING[productType]
      );

    default:
      return PRODUCT_TYPE_NAME_MAPPING[productType];
  }
};

const MarketplaceProductCard = ({
  badgeText,
  ctaHref,
  ctaSubtext,
  ctaText,
  data,
  heading,
  history,
  identity,
  includeBadge,
  includeWhyThisProduct,
  isUnstructuredVertical,
  keyFacts,
  omitRating,
  productCardImage,
  productType,
  dataDependency,
  starRating,
  whyThisProductMethodologyUrl,
  whyThisProductText,
  whyThisProductTypeOverride,
}) => {
  const offers = Object.values(_.get(data, 'value', {}));
  const requestedId = _.get(dataDependency, 'data.ids[0].id', null);

  const marketplaceEntity = getOfferById(requestedId, offers);

  if (_.isEmpty(marketplaceEntity) && !isUnstructuredVertical) {
    return null;
  }

  const cardIdentifier = _.isEmpty(marketplaceEntity)
    ? heading
    : getProductName(marketplaceEntity);

  // Allow heading override for BNK.
  const isBanking = dataDependency.type === PRODUCT_TYPES.BANKING;
  const headingProps = isBanking && heading ? { heading } : {};

  const badgeProps = includeBadge
    ? {
        hasPreheader: true,
        preheaderText: badgeText,
        preheaderType: PREHEADER_TYPE.BADGE,
      }
    : {};

  // hack for SHOP-9808 + hack for MR-2915
  const slug = getSlug(_.get(history, 'location.pathname', ''));
  const shouldUpdateSharedId =
    dataDependency.type === DATA_DEPENDENCY_TYPES.CREDIT_CARDS &&
    slug === 'awards-credit-cards-2020' &&
    getLegacyId(marketplaceEntity) === 6050;
  const shouldAddMortgageUtm =
    dataDependency.type === DATA_DEPENDENCY_TYPES.MORTGAGES &&
    slug === 'awards-mortgages-2022' &&
    !isNonMonetizing(marketplaceEntity);
  const applyLink = getApplyLink(marketplaceEntity);
  const delimeter = applyLink && applyLink.includes('?') ? '&' : '?';
  const prevLinkPropsCheck = shouldUpdateSharedId
    ? {
        ctaHref: `${applyLink}${delimeter}sharedid=4664157C`,
      }
    : {};
  const linkProps = shouldAddMortgageUtm
    ? {
        ctaHref: `${applyLink}${delimeter}partnerParams=utm_campaign%3Dnerdwallet_content`,
      }
    : prevLinkPropsCheck;

  // Omit tabs as needed.
  const omittedTabs = [
    // Exclude 'why we like it' tab since it largely duplicates the
    // `why this product` section.
    CONTENT_TABS.WHY_WE_LIKE_IT,
  ];

  const renderWhyThisProduct = () => (
    <div
      className={classNames([
        styles['why-this-product'],
        nds('margin-bottom-2'),
      ])}
    >
      <Text size="small" component="h4">
        Why this{' '}
        {whyThisProductTypeOverride ||
          getProductName(marketplaceEntity) ||
          'product'}{' '}
        made the list
      </Text>
      <Text component="p">{whyThisProductText}</Text>
      <Link
        target="_blank"
        href={setTrk({
          url: whyThisProductMethodologyUrl,
          params: {
            [TrkTag.copy]: 'Read our methodology',
            [TrkTag.element]: TrkElement.hyperlink,
            [TrkTag.location]: 'MarketplaceProductCard__whyThisProduct',
            trk_productId: getId(marketplaceEntity),
          },
        })}
      >
        Read our methodology
      </Link>
    </div>
  );

  const expandableContentProps = { entityName: 'marketplace_product_card' };

  const analytics = {
    enabled: true,

    // empty object required for product_impression
    productImpressionProps: {},

    // expandableContentProps is duplicated because MPC takes separate props
    // for desktop vs. mobile
    contentProps: { expandableContentProps },
    expandableContentProps,
  };

  if (!isUnstructuredVertical) {
    return (
      <div className={styles.marketplaceProductCard}>
        <MarketplaceProductCardImpression
          analytics={analytics}
          displayPage={DISPLAY_PAGES.DIGITAL_MARKETING}
          hasAccordionOnMobile
          identity={identity}
          marketplaceEntity={marketplaceEntity}
          omitRating={omitRating}
          omittedTabs={_.compact([
            ...omittedTabs,
            // Omit `key facts` tab for Personal Loans.
            getProductType(marketplaceEntity) === PRODUCT_TYPES.PERSONAL_LOANS
              ? CONTENT_TABS.KEY_FACTS
              : undefined,
          ])}
          {...headingProps}
          {...badgeProps}
          {...linkProps}
        />
        {includeWhyThisProduct ? renderWhyThisProduct() : null}
      </div>
    );
  }

  /**
   * MarketplaceProductCard uses the 'ProductCtas' and 'MarketplaceApplyNow' components, which create
   * an impression event based off of the 'marketplaceEntity' object (which unstructured
   * verticals do not have).
   * In order to track the 'ProductCtas'/'MarketplaceApplyNow' impression, the tracking event logic
   * assumes a 'vertical' property is set from the 'marketplaceEntity', and tries to set an
   * 'eventName' based off of this unset string which causes the MarketplaceProductCard to crash.
   * The fix is to pass 'shouldLogImpression: false' so that impressions are not logged through the
   * 'ProductCtas'/'MarketplaceApplyNow' components, and wrap unstructured verticals in
   * 'ElementImpression' in order to be tracked.
   * Link to a conversation about the problem: https://nerdwallet.slack.com/archives/CDPF7S56K/p1572472820003600
   */
  const keyFactsList = !_.isEmpty(keyFacts) ? (
    <List copyStyle="text-default">
      {keyFacts.map((keyFact) => (
        <Li key={keyFact.$unique_key}>{keyFact.text}</Li>
      ))}
    </List>
  ) : null;

  const productImage = productCardImage && {
    src: productCardImage,
    alt: heading,
  };

  return (
    <ElementImpression
      elementContent={cardIdentifier}
      elementName="MarketplaceProductCard"
      id={`MarketplaceProductCard--${heading}-${ctaHref}-${ctaText}`}
    >
      <div className={styles.marketplaceProductCard}>
        <BaseMarketplaceProductCard
          ctaHref={ctaHref}
          ctaSubtext={ctaSubtext}
          ctaText={ctaText}
          displayPage={DISPLAY_PAGES.DIGITAL_MARKETING}
          hasAccordionOnMobile
          heading={heading}
          identity={identity}
          imageData={productImage}
          keyFacts={keyFactsList}
          omitRating={omitRating}
          omittedTabs={omittedTabs}
          productType={productType}
          shouldLogImpression={false}
          // star Rating is saved as a string
          // and must be passed as a number to ReactMarketplaceProductCard
          starRating={_.toNumber(starRating)}
          {...badgeProps}
        />
        {includeWhyThisProduct ? renderWhyThisProduct() : null}
      </div>
    </ElementImpression>
  );
};

MarketplaceProductCard.propTypes = {
  badgeText: PropTypes.string,
  ctaHref: PropTypes.string,
  ctaSubtext: PropTypes.string,
  ctaText: PropTypes.string,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
  dataDependency: PropTypes.shape({
    type: PropTypes.string,
    ids: PropTypes.string,
    productPage: PropTypes.string,
  }),
  hasWhyThisProduct: PropTypes.bool,
  heading: PropTypes.string,
  history: PropTypes.object,
  identity: PropTypes.object,
  includeBadge: PropTypes.bool,
  includeWhyThisProduct: PropTypes.bool,
  isUnstructuredVertical: PropTypes.bool,
  keyFacts: PropTypes.arrayOf(PropTypes.string),
  omitRating: PropTypes.bool,
  productCardImage: PropTypes.string,
  productType: PropTypes.oneOf(_.values(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL)),
  starRating: PropTypes.string,
  whyThisProductMethodologyUrl: PropTypes.string,
  whyThisProductText: PropTypes.string,
  whyThisProductTypeOverride: PropTypes.string,
};

MarketplaceProductCard.defaultProps = {
  badgeText: '',
  data: {},
  dataDependency: {},
  hasWhyThisProduct: false,
  history: {},
  identity: {},
  includeBadge: false,
  isUnstructuredVertical: false,
  omitRating: false,
};

MarketplaceProductCard.getEditingConfig = (componentDocument) => {
  const isUnstructuredVertical = _.get(
    componentDocument,
    'props.isUnstructuredVertical',
    false
  );
  const isBanking =
    _.get(componentDocument, 'dataDependency.type') === PRODUCT_TYPES.BANKING;

  const headingInputConfig = stringInputSpec('heading', 'Product Name');
  const unstructedVerticalConfig = [
    stringInputSpec('ctaHref', 'Cta href'),
    stringInputSpec('ctaSubtext', 'Cta subtext'),
    stringInputSpec('ctaText', 'Cta text'),
    headingInputConfig,
    repeatable('keyFacts', 'Key Facts', 'keyFact', () => [
      stringInputSpec('text', 'Key Fact'),
    ]),
    stringInputSpec('productCardImage', 'Product card image'),
    stringSelectSpec(
      'productType',
      'Product type',
      _.map(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL, (productType) => ({
        label: _.startCase(productType),
        value: productType,
      }))
    ),
    stringInputSpec('starRating', 'Star rating'),
  ];
  const dataDependencyConfig = [
    dataDependencyTypePicker,
    dataDependencyIdInput,
  ];

  const includeBadge = _.get(componentDocument, 'props.includeBadge', false);
  const badgeConfig = [
    boolCheckboxSpec('includeBadge', 'Include Best-of Badge?'),
    includeBadge && stringInputSpec('badgeText', 'Badge Text'),
  ];

  const includeWhyThisProduct = _.get(
    componentDocument,
    'props.includeWhyThisProduct',
    false
  );
  const whyThisProductConfig = [
    boolCheckboxSpec(
      'includeWhyThisProduct',
      "Include 'Why this product' section?",
      {
        help: 'Include a section below the product card explaining why this product is a Best of Award',
      }
    ),
    ...(includeWhyThisProduct
      ? [
          stringInputSpec('whyThisProductText', 'Product explanation', {
            help: 'Short text explaining why this product made the list',
          }),
          stringInputSpec('whyThisProductMethodologyUrl', 'Methodology URL'),
          stringInputSpec(
            'whyThisProductTypeOverride',
            'Override product type',
            {
              help: "Override the category name to use in the 'Why this product' title",
            }
          ),
        ]
      : []),
  ];

  return [
    boolCheckboxSpec(
      'isUnstructuredVertical',
      'Is this an unstructured vertical?',
      {
        help: 'Check to manually input information for a vertical without data in marketplace (Taxes, Travel, Auto Insurance)',
      }
    ),
    // Show 1. unstructured vertical fields or 2. data dependency fields.
    ...(isUnstructuredVertical
      ? unstructedVerticalConfig
      : dataDependencyConfig),
    // Show heading override field if BNK and not already showing for unstructured vertical.
    !isUnstructuredVertical && isBanking && headingInputConfig,
    ...badgeConfig,
    boolCheckboxSpec('omitRating', 'Omit star rating?'),
    ...whyThisProductConfig,
  ].filter(Boolean); // Remove empty/falsey values to ensure test parity.
};

const dataDependencies = (props) => {
  const type = _.get(props, 'dataDependency.type');
  if (!type) {
    return null;
  }

  const id = _.get(props, 'dataDependency.data.ids[0].id');
  const ids = id ? [id] : [];

  return {
    data: {
      type,
      ids,
    },
  };
};

const WrappedComponent = compose(
  withRouter,
  withMarketplaceContext,
  withOutOfDateMessaging,
  connect(
    createStructuredSelector({
      identity: _.property('identity'),
    })
  )
)(MarketplaceProductCard);

export default withDataDependencies(WrappedComponent, dataDependencies);
